/**
 * Validation rules shared by the API and the admin UI, so both reject
 * exactly the same inputs with exactly the same messages (see the "Upload a file" and
 * "Create a new client" sections).
 */

/** Client IDs and subfolder names: lowercase slug, single dashes, one path segment. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const CLIENT_ID_MAX_LENGTH = 63;
export const SUBFOLDER_MAX_LENGTH = 63;
export const FILENAME_MAX_LENGTH = 255;
export const DISPLAY_NAME_MAX_LENGTH = 100;
export const NOTES_MAX_LENGTH = 500;
export const METADATA_MAX_BYTES = 4096;
export const IDEMPOTENCY_KEY_MAX_LENGTH = 255;

export type ValidationResult = { ok: true } | { ok: false; reason: string };

const OK: ValidationResult = { ok: true };
const fail = (reason: string): ValidationResult => ({ ok: false, reason });

export function validateClientId(value: unknown): ValidationResult {
  if (typeof value !== 'string' || value.length === 0) return fail('Client ID is required.');
  if (value.length > CLIENT_ID_MAX_LENGTH)
    return fail(`Client ID must be ${CLIENT_ID_MAX_LENGTH} characters or fewer.`);
  if (!SLUG_PATTERN.test(value))
    return fail(
      'Use lowercase letters, digits and single dashes only — no spaces, no leading or trailing dash.',
    );
  return OK;
}

export function validateSubfolder(value: unknown): ValidationResult {
  if (value === undefined || value === null || value === '') return OK; // means "root"
  if (typeof value !== 'string') return fail('Subfolder must be a string.');
  if (value.length > SUBFOLDER_MAX_LENGTH)
    return fail(`Subfolder must be ${SUBFOLDER_MAX_LENGTH} characters or fewer.`);
  if (!SLUG_PATTERN.test(value))
    return fail(
      'Subfolder must use lowercase letters, digits and single dashes only, as one path segment.',
    );
  return OK;
}

/**
 * A filename becomes part of a real filesystem path, so it is validated, never
 * sanitized-and-accepted (see "Filename rules" in the "Upload a file" section).
 */
export function validateFileName(value: unknown): ValidationResult {
  if (typeof value !== 'string' || value.length === 0) return fail('A filename is required.');
  if (value.length > FILENAME_MAX_LENGTH)
    return fail(`Filename must be ${FILENAME_MAX_LENGTH} characters or fewer.`);
  if (value === '.' || value === '..') return fail('Filename must not be "." or "..".');
  if (value.includes('/') || value.includes(String.fromCharCode(92)))
    return fail('Filename must not contain a path separator.');
  if (value.includes(String.fromCharCode(0))) return fail('Filename must not contain a NUL byte.');
  if (value.includes('..')) return fail('Filename must not contain "..".');
  return OK;
}

export function validateNotes(value: unknown): ValidationResult {
  if (value === undefined || value === null || value === '') return OK;
  if (typeof value !== 'string') return fail('Notes must be text.');
  if (value.length > NOTES_MAX_LENGTH)
    return fail(`Notes must be ${NOTES_MAX_LENGTH} characters or fewer.`);
  return OK;
}

export function validateDisplayName(value: unknown): ValidationResult {
  if (typeof value !== 'string' || value.trim().length === 0)
    return fail('Display name is required.');
  if (value.length > DISPLAY_NAME_MAX_LENGTH)
    return fail(`Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer.`);
  return OK;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function validateEmail(value: unknown): ValidationResult {
  if (typeof value !== 'string' || value.trim().length === 0) return fail('Email is required.');
  if (!EMAIL_PATTERN.test(normalizeEmail(value))) return fail('Enter a valid email address.');
  return OK;
}

export function validateIdempotencyKey(value: unknown): ValidationResult {
  if (value === undefined || value === null) return OK;
  if (typeof value !== 'string' || value.length === 0)
    return fail('X-Idempotency-Key must be a non-empty string.');
  if (value.length > IDEMPOTENCY_KEY_MAX_LENGTH)
    return fail(`X-Idempotency-Key must be ${IDEMPOTENCY_KEY_MAX_LENGTH} characters or fewer.`);
  // Printable ASCII only: the server never parses it, but a header must stay a header.
  if (!/^[\x20-\x7E]+$/.test(value))
    return fail('X-Idempotency-Key must contain printable ASCII characters only.');
  return OK;
}

/**
 * Parses and re-serializes the caller's `metadata` field, enforcing the 4 KB cap
 * on the compact form (see the "Upload a file" section).
 */
export function parseMetadata(
  raw: unknown,
): { ok: true; value: unknown } | { ok: false; reason: string } {
  if (raw === undefined || raw === null || raw === '') return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, reason: 'metadata must be a JSON string.' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'metadata must be valid JSON.' };
  }
  const compact = JSON.stringify(parsed);
  if (compact === undefined) return { ok: false, reason: 'metadata must be valid JSON.' };
  if (Buffer.byteLength(compact, 'utf8') > METADATA_MAX_BYTES)
    return { ok: false, reason: `metadata must be ${METADATA_MAX_BYTES} bytes or fewer.` };
  return { ok: true, value: parsed };
}
